import { useMemo, useState } from "react";
import GridLayout from "react-grid-layout";
import uniqBy from "lodash/uniqBy";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
// Material-UI
import {
	Box,
	ClickAwayListener,
	Container,
	Fab,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	Popper,
	Typography,
} from "@mui/material";

// Dashboard Components
import PageLayer from "@/Layouts/views/PageLayer";
import WidgetsWrapper from "./components/WidgetsWrapper";

// Widgets Icons
import MyRTCIcon from "@/assets/icons/dashboard/MyRTC.svg";
import CustomerSatisfactionIcon from "@/assets/icons/dashboard/csi-performance.svg";
import OnlineRatingsIcon from "@/assets/icons/dashboard/online-ratings.svg";
import UrgentActionsIcon from "@/assets/icons/dashboard/urgent_actions.svg";
import TipsAndTricksIcon from "@mui/icons-material/TipsAndUpdates";
import UrgentActionsFooterPointGreyIcon from "@/assets/icons/dashboard/point_grey.svg";

// Hooks
import { useManagedPositions } from "@/Common/hooks/Positions";

// Types
import { ILocationState } from "@/Common/interfaces/location";
import { IDashboardLayout, IUserState } from "@/Common/interfaces/user";

// API
import { getUrgentActions } from "./api/queries";

// Widgets
import BirthdaysOfMonth from "./components/BirthdaysOfMonth";
import CustomerSatisfaction from "./components/CustomerSatisfaction/CustomerSatisfaction";
import MyRTC from "./components/MyRTC";
import Notifications from "./components/NotificationsWidget";
import OnlineRatings from "./components/OnlineRatings";
import WelcomeUser from "./components/WelcomeUser";
import SalespersonOfMonth from "./components/SalespersonOfMonth";
import TipsAndTricks from "./components/TipsAndTricks";
import UrgentActions from "./components/UrgentActions/UrgentActions";

import "../../node_modules/react-grid-layout/css/styles.css";
import "../../node_modules/react-resizable/css/styles.css";
import "./styles/index.css";

import { ReactComponent as OrganizeIcon } from "../assets/icons/dashboard/Organize.svg";
import { ReactComponent as SyncIcon } from "../assets/icons/dashboard/syncicon.svg";
import { ReactComponent as FinishIcon } from "../assets/icons/dashboard/finish.svg";
import { ReactComponent as SystemSettingsIcon } from "@/assets/icons/header/settings/SystemSettingsIcon.svg";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { storageGetItem } from "@/Common/helpers/Storage";
import { useMutation } from "@tanstack/react-query";
import { updateUser } from "@/User/api/UserPreferences/mutations";
import { actions } from "@/redux/reducers/User";

const Dashboard = () => {
	const dispatch = useDispatch();
	const [internalLayout, setInternalLayout] = useState<IDashboardLayout[]>([]);
	const [organizeMode, setOrganizeMode] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const user = useSelector((state: IUserState) => state.user.user);
	const locationId = useSelector((state: ILocationState) => state.location.location?.locationId);
	const urgentActions = getUrgentActions(locationId);

	const { mutate } = useMutation({
		mutationKey: ["handleLayoutChange"],
		mutationFn: (input: { dashboardLayout: IDashboardLayout[] }) => updateUser(user?._id, user?.locationId, input),
	});

	const { isPositionManaged } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const IS_POSITION_MANAGED = isPositionManaged(user?.position);

	const widgetList = useMemo(() => {
		const originalList = [
			{
				key: "my-dealer-wave",
				component: <MyRTC />,
				icon: <img width={15} src={MyRTCIcon} alt="" />,
				title: "My Dealerwave",
				hidden: !IS_POSITION_MANAGED,
				static: true,
			},
			{
				key: "urgent-actions",
				component: <UrgentActions actionsList={urgentActions} />,
				icon: <img width={25} src={UrgentActionsIcon} alt="" />,
				title: "Urgent Actions",
				elevated: true,
				// Hidden until we have a real implementation of the backend
				hidden: true,
				footerIcon: UrgentActionsFooterPointGreyIcon,
				footerText: "Requires immediate attention",
				static: true,
			},
			{
				key: "notifications",
				component: <Notifications />,
				icon: null,
				title: "Notifications",
				elevated: false,
				static: true,
			},
			{
				key: "customer-satisfaction",
				component: <CustomerSatisfaction />,
				icon: <img width={25} src={CustomerSatisfactionIcon} alt="" />,
				title: "Customer Satisfaction",
				static: false,
			},
			{
				key: "tips-and-tricks",
				component: <TipsAndTricks />,
				icon: <TipsAndTricksIcon sx={{ color: "var(--icon-widget-color-yellow)", fontSize: 30 }} />,
				title: "Tips and Tricks",
				elevated: true,
				static: false,
			},
			{
				key: "online-ratings",
				component: <OnlineRatings />,
				icon: <img width={25} src={OnlineRatingsIcon} alt="" />,
				title: "Store Online Ratings",
				static: false,
				hidden: false,
			},
			{
				key: "salesperson-of-month",
				component: <SalespersonOfMonth />,
				elevated: true,
				disablePadding: true,
				static: false,
			},
			{
				key: "birthdays-of-month",
				component: <BirthdaysOfMonth />,
				elevated: true,
				static: false,
			},
		];
		return originalList.filter((item) => !item.hidden);
	}, [IS_POSITION_MANAGED, urgentActions, user]);

	const baseLayout = useMemo(() => {
		const originalList = [
			// First Column
			{ i: "my-dealer-wave", x: 0, y: 0, w: 1, h: 3.3, isResizable: false, static: true },
			{ i: "customer-satisfaction", x: 0, y: 1, w: 1, h: 3.3, isResizable: false },
			{ i: "salesperson-of-month", x: 0, y: 2, w: 1, h: 2.5, isResizable: false },
			{ i: "birthdays-of-month", x: 0, y: 3, w: 1, h: 3.4, isResizable: false },
			// Second Column
			{ i: "urgent-actions", x: 1, y: 0, w: 1, h: 3.1, isResizable: false, static: true },
			{ i: "tips-and-tricks", x: 1, y: 1, w: 1, h: 3.2, isResizable: false },
			// Third Column
			{ i: "notifications", x: 2, y: 0, w: 1, h: 5, isResizable: false, static: true },
			{ i: "online-ratings", x: 2, y: 1, w: 1, h: 4.2, isResizable: false },
		].filter((item) => {
			const foundWidget = widgetList.find((w) => w.key === item.i);
			return !!foundWidget;
		});

		const storageLayout = storageGetItem("dashboard-layout") as GridLayout.Layout[];
		if (storageLayout) {
			return uniqBy([...storageLayout, ...originalList], "i");
		} else {
			return originalList;
		}
	}, [widgetList, user]);

	const handleLayoutChange = (params: IDashboardLayout[]) => {
		setInternalLayout(params);
	};

	const handleDrag = (
		_layout: GridLayout.Layout[],
		_oldItem: GridLayout.Layout,
		_newItem: GridLayout.Layout,
		_placeholder: GridLayout.Layout,
		_e: MouseEvent,
		element: HTMLElement
	) => {
		element.classList.add("!rotate-6");
		element.classList.add("!z-[9999]");
		element.classList.add("!cursor-grabbing");
		const placeholders = document.querySelectorAll(".react-grid-placeholder");
		const placeholder = placeholders[0] as HTMLElement;
		placeholder.style.opacity = "1";
	};

	const handleDragStart = () => {
		document.body.style.userSelect = "none";
	};

	const handleDragStop = () => {
		document.body.style.userSelect = "auto";
	};

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const handleClose = () => {
		if (!organizeMode) {
			setAnchorEl(null);
		}
	};

	const open = Boolean(anchorEl);

	const id = open ? "layout-popper-menu" : undefined;

	const handleOrganizeMode = () => {
		setOrganizeMode((prev) => !prev);
		if (organizeMode) {
			dispatch(actions.SET_USER({ user: { ...user, dashboardLayout: internalLayout } }));
			mutate({ dashboardLayout: internalLayout });
		}
	};

	return (
		<PageLayer title={<WelcomeUser />} icon={""} align="center">
			<Container disableGutters maxWidth={false} className="main-area">
				<Box className="w-full p-[25px]">
					<GridLayout
						cols={3}
						rowHeight={100}
						width={1390}
						onDrag={handleDrag}
						useCSSTransforms={false}
						onDragStart={handleDragStart}
						onDragStop={handleDragStop}
						onLayoutChange={handleLayoutChange}
						isDraggable={organizeMode}
					>
						{widgetList.map((widget) => {
							const isDraggable = organizeMode && !widget.static;
							const {
								x,
								y,
								w,
								h,
								static: staticMode,
								isResizable,
							} = baseLayout?.find((layout) => layout.i === widget.key) ?? {};
							return (
								<Box
									key={widget.key}
									style={{ cursor: isDraggable ? "grab" : "auto" }}
									data-grid={{ x, y, w, h, isResizable, static: staticMode }}
								>
									<Box className={isDraggable ? "pointer-events-none" : ""}>
										<WidgetsWrapper
											isDraggable={isDraggable}
											key={widget.key}
											icon={widget.icon}
											title={widget.title}
											isElevated={widget.elevated}
											disablePadding={widget.disablePadding}
											footerIcon={widget.footerIcon}
											footerText={widget.footerText}
										>
											{widget.component}
										</WidgetsWrapper>
									</Box>
								</Box>
							);
						})}
					</GridLayout>
					<Box className="flex justify-end items-center">
						<Fab
							onClick={handleClick}
							className={open ? "z-20 !bg-neutral-gray" : "z-20 !bg-default"}
							sx={{ position: "relative", width: "40px", height: "40px", boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.20)" }}
							style={{
								padding: 0,
								zIndex: 1100,
								position: "fixed",
								bottom: 10,
								right: 70,
								top: "unset",
								left: "unset",
								visibility: "visible",
							}}
						>
							<Box
								className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
									open ? "opacity-0" : "opacity-100"
								}`}
							>
								<OrganizeIcon className="fill-white w-[20px] h-[20px]" />
							</Box>
							<Box
								className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
									open ? "opacity-100" : "opacity-0"
								}`}
							>
								<CloseRoundedIcon sx={{ width: "25px", height: "25px", color: "white" }} />
							</Box>
						</Fab>
						<Popper
							id={id}
							open={open}
							anchorEl={anchorEl}
							placement="top-end"
							className="!z-[10]"
							modifiers={[
								{
									name: "offset",
									options: {
										offset: [0, 10],
									},
								},
							]}
						>
							<ClickAwayListener onClickAway={() => handleClose()}>
								<motion.div
									initial={{ scale: 0, opacity: 0, x: 100, y: 100 }}
									animate={{ scale: 1, opacity: 1, x: 0, y: 0 }}
									exit={{ scale: 0, opacity: 0, x: 100, y: 100 }}
									transition={{
										type: "spring",
										stiffness: 260,
										damping: 20,
									}}
								>
									<Paper className="!rounded-[10px]" style={{ boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.20)" }}>
										<List>
											<ListItem disablePadding>
												<ListItemButton disabled={organizeMode}>
													<ListItemIcon>
														<SystemSettingsIcon />
													</ListItemIcon>
													<ListItemText className="ml-[-20px]">
														<Typography className="!font-montserrat !text-sm !font-medium !text-default">
															Manage Widgets
														</Typography>
													</ListItemText>
												</ListItemButton>
											</ListItem>
											<ListItem disablePadding>
												<ListItemButton onClick={handleOrganizeMode}>
													<ListItemIcon>
														{organizeMode ? (
															<FinishIcon className="fill-default w-[21px] h-[21px]" />
														) : (
															<SyncIcon className="fill-default w-[21px] h-[21px]" />
														)}
													</ListItemIcon>
													<ListItemText className="ml-[-20px]">
														<Typography className="!font-montserrat !text-sm !font-medium !text-default">
															{organizeMode ? "Finish" : "Reorganize"}
														</Typography>
													</ListItemText>
												</ListItemButton>
											</ListItem>
										</List>
									</Paper>
								</motion.div>
							</ClickAwayListener>
						</Popper>
					</Box>
				</Box>
			</Container>
		</PageLayer>
	);
};

export default Dashboard;
